import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Ticket } from '../api/entities.js';
import { User } from '../api/entities.js';
import { UploadFile } from '../api/integrations.js';
import { Button } from "../components/ui/button.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { ArrowLeft, Send, Upload, Paperclip, X } from 'lucide-react';

export default function TicketComment() {
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('id');
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState(null);
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, [ticketId]);

  const loadData = async () => {
    if (!ticketId) {
      navigate(createPageUrl('Tickets'));
      return;
    }

    try {
      const [ticketData, userData] = await Promise.all([
        Ticket.findById(ticketId),
        User.me()
      ]);
      
      setTicket(ticketData);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading data:', error);
      navigate(createPageUrl('Tickets'));
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const { file_url } = await UploadFile({ file });
        uploadedFiles.push({
          name: file.name,
          url: file_url,
          size: file.size,
          type: file.type
        });
      }
      setAttachments([...attachments, ...uploadedFiles]);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Erro ao fazer upload dos arquivos. Por favor, tente novamente.');
    }
    setUploading(false);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    if ((!content.trim() && attachments.length === 0) || !currentUser) return;
    
    setIsLoading(true);
    try {
      const currentTicket = await Ticket.findById(ticketId);
      
      const newInteraction = {
        type: 'comment',
        user_id: currentUser.id,
        content: content,
        timestamp: new Date().toISOString(),
        attachments: attachments
      };
      
      await Ticket.update(ticketId, {
        ...currentTicket,
        interactions: [...(currentTicket.interactions || []), newInteraction]
      });
      
      navigate(createPageUrl(`ViewTicket?id=${ticketId}`));
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erro ao adicionar comentário. Por favor, tente novamente.');
    }
    setIsLoading(false);
  };

  if (!ticket || !currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl(`ViewTicket?id=${ticketId}`))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Adicionar Comentário</h1>
          <p className="text-muted-foreground">
            Chamado #{ticket.id?.substring(0, 8)}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {ticket.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Digite seu comentário aqui..."
            className="min-h-[200px] resize-none"
          />

          {attachments.length > 0 && (
            <div className="space-y-2">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-muted/50 p-2 rounded">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAttachment(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between pt-4">
            <div>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileUpload}
                multiple
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outline"
                  disabled={uploading}
                  className="gap-2"
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Enviando...' : 'Anexar Arquivos'}
                  </span>
                </Button>
              </label>
            </div>

            <Button 
              onClick={handleSubmit}
              disabled={isLoading || (!content.trim() && attachments.length === 0)}
              className="gap-2"
            >
              <Send className="w-4 h-4" />
              {isLoading ? 'Enviando...' : 'Enviar Comentário'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}